import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { Calendar } from "@/components/ui/Calendar/Calendar";
import { Label } from "@/components/ui/Label/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover/Popover";
import { ChevronDownIcon, CircleMinus, OctagonX } from "lucide-react";
import { format } from "date-fns";
import { SelectStyles } from "@/components/ui/Select/Select";
import { z } from "zod";
import { useRotations } from "../../hooks/rotations/useRotations";
import {
  Controller,
  useFieldArray,
  useForm,
  type SubmitHandler,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HStack } from "@/components/layout/HStack";
import { Switch } from "@/components/ui/Switch/Switch";
import { Input } from "@/components/ui/Input/Input";
import { Separator } from "@/components/ui/Separator/Separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import type { Dispatch, SetStateAction } from "react";

const schema = z
  .object({
    date: z.date("A date is required"),
    teachersCanCreate: z.boolean("Teachers can create must be true or false"),
    studentsCanView: z
      .object({
        from: z.date("A from date is required"),
        to: z.date("A to date is required"),
        fromTime: z
          .string("Time must be a string")
          .regex(/^\d{2}:\d{2}(:\d{2})?$/, "Invalid time"),
        toTime: z
          .string("Time must be a string")
          .regex(/^\d{2}:\d{2}(:\d{2})?$/, "Invalid time"),
      })
      .optional(),
    studentsCanEdit: z
      .object({
        from: z.date("A from date is required"),
        to: z.date("A to date is required"),
        fromTime: z
          .string("Time must be a string")
          .regex(/^\d{2}:\d{2}(:\d{2})?$/, "Invalid time"),
        toTime: z
          .string("Time must be a string")
          .regex(/^\d{2}:\d{2}(:\d{2})?$/, "Invalid time"),
      })
      .optional(),
    sessions: z
      .array(
        z
          .object({
            name: z
              .string("Session name must be a string")
              .min(1, "A session name is required")
              .max(120, "Session name cannot exceed 120 characters"),
            singleton: z.boolean("Singleton must be true or false"),
            selectedDates: z.array(z.date("Invalid date")).optional(),
          })
          .superRefine((session, ctx) => {
            if (
              session.singleton === false &&
              (!session.selectedDates || session.selectedDates.length < 1)
            ) {
              ctx.addIssue({
                path: ["selectedDates"],
                message: "At least one date for session is required",
                code: "custom",
              });
            }
          }),
      )
      .min(1, "At least one session is required"),
  })
  .superRefine((data, ctx) => {
    if (!data.studentsCanView) {
      ctx.addIssue({
        path: ["studentsCanView"],
        message: "Students can view is required",
        code: "custom",
      });
    }
    if (!data.studentsCanEdit) {
      ctx.addIssue({
        path: ["studentsCanEdit"],
        message: "Students can edit is required",
        code: "custom",
      });
    }
  });

export type CreateFormFields = z.infer<typeof schema>;

const CreateRotationForm = ({
  onSuccess,
  setRotations,
}: {
  onSuccess: () => void;
  setRotations: Dispatch<SetStateAction<{ rotations: any[]; sessions: any[] }>>;
}) => {
  const { createRotation } = useRotations();
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateFormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: undefined,
      teachersCanCreate: false,
      studentsCanView: {
        from: undefined,
        to: undefined,
        fromTime: "08:00",
        toTime: "16:00",
      },
      studentsCanEdit: {
        from: undefined,
        to: undefined,
        fromTime: "08:00",
        toTime: "16:00",
      },
      sessions: [{ name: "", singleton: false, selectedDates: [] }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "sessions",
  });

  const sessions = watch("sessions");

  const onSubmit: SubmitHandler<CreateFormFields> = async (data) => {
    const created = await createRotation(data, onSuccess);
    if (!created) return;
    setRotations((prev) => ({
      rotations: [...prev.rotations, created.rotation],
      sessions: [...prev.sessions, ...created.sessions],
    }));
  };

  return (
    <>
      <SheetBody>
        <form id="create-rotation-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Date</Label>
                {errors.date && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{errors.date.message}</TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="date"
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        id="date"
                        aria-invalid={errors.date ? true : false}
                        className={SelectStyles.trigger}
                        data-placeholder={field.value ? undefined : ""}
                      >
                        {field.value
                          ? format(field.value, "MMMM dd, yyyy")
                          : "Select date"}
                        <ChevronDownIcon className={SelectStyles.triggerIcon} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      style={{ width: "auto", overflow: "hidden", padding: 0 }}
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        captionLayout="dropdown"
                        onSelect={(date) => {
                          field.onChange(date);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
            </VStack>
            <HStack align="center">
              <HStack gap="0.25rem">
                <Label>Teachers Can Create</Label>
                {errors.teachersCanCreate && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.teachersCanCreate.message}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="teachersCanCreate"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={(val) => field.onChange(val)}
                    aria-invalid={errors.teachersCanCreate ? true : false}
                  />
                )}
              />
            </HStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Students Can View</Label>
                {(errors.studentsCanView?.to ||
                  errors.studentsCanView?.from) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.studentsCanView.to
                        ? errors.studentsCanView.to.message
                        : errors.studentsCanView.from
                          ? errors.studentsCanView.from.message
                          : ""}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="studentsCanView"
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        id="date"
                        className={SelectStyles.trigger}
                        data-placeholder={field.value?.from ? undefined : ""}
                        aria-invalid={
                          errors.studentsCanView?.from ||
                          errors.studentsCanView?.to
                            ? true
                            : false
                        }
                      >
                        {field.value?.from
                          ? `${format(field.value.from, "MMMM dd, yyyy")} - ${format(field.value.to, "MMMM dd, yyyy")}`
                          : "Select dates"}
                        <ChevronDownIcon className={SelectStyles.triggerIcon} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      style={{ width: "auto", overflow: "hidden", padding: 0 }}
                      align="start"
                    >
                      <Calendar
                        mode="range"
                        selected={
                          field.value
                            ? { from: field.value.from, to: field.value.to }
                            : undefined
                        }
                        captionLayout="dropdown"
                        onSelect={(range) => {
                          field.onChange({
                            ...(field.value ?? {
                              from: undefined,
                              to: undefined,
                              fromTime: "08:00",
                              toTime: "16:00",
                            }),
                            from: range?.from,
                            to: range?.to,
                          });
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
            </VStack>
            <HStack align="center">
              <Input
                type="time"
                step={1}
                {...register("studentsCanView.fromTime")}
                aria-invalid={errors.studentsCanView?.fromTime ? true : false}
              />
              —
              <Input
                type="time"
                step={1}
                {...register("studentsCanView.toTime")}
                aria-invalid={errors.studentsCanView?.toTime ? true : false}
              />
            </HStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Students Can Edit</Label>
                {(errors.studentsCanEdit?.to ||
                  errors.studentsCanEdit?.from) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.studentsCanEdit.to
                        ? errors.studentsCanEdit.to.message
                        : errors.studentsCanEdit.from
                          ? errors.studentsCanEdit.from.message
                          : ""}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="studentsCanEdit"
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        id="date"
                        className={SelectStyles.trigger}
                        data-placeholder={field.value?.from ? undefined : ""}
                        aria-invalid={
                          errors.studentsCanEdit?.from ||
                          errors.studentsCanEdit?.to
                            ? true
                            : false
                        }
                      >
                        {field.value?.from
                          ? `${format(field.value.from, "MMMM dd, yyyy")} - ${format(field.value.to, "MMMM dd, yyyy")}`
                          : "Select dates"}
                        <ChevronDownIcon className={SelectStyles.triggerIcon} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      style={{ width: "auto", overflow: "hidden", padding: 0 }}
                      align="start"
                    >
                      <Calendar
                        mode="range"
                        selected={
                          field.value
                            ? { from: field.value.from, to: field.value.to }
                            : undefined
                        }
                        captionLayout="dropdown"
                        onSelect={(range) => {
                          field.onChange({
                            ...(field.value ?? {
                              from: undefined,
                              to: undefined,
                              fromTime: "08:00",
                              toTime: "16:00",
                            }),
                            from: range?.from,
                            to: range?.to,
                          });
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
            </VStack>
            <HStack align="center">
              <Input
                type="time"
                step={1}
                {...register("studentsCanEdit.fromTime")}
                aria-invalid={errors.studentsCanEdit?.fromTime ? true : false}
              />
              —
              <Input
                type="time"
                step={1}
                {...register("studentsCanEdit.toTime")}
                aria-invalid={errors.studentsCanEdit?.toTime ? true : false}
              />
            </HStack>
            <Separator />
            <VStack>
              <HStack justify="space-between">
                <Label>Sessions</Label>
                <Label>Singleton</Label>
              </HStack>
              {fields.map((f, index) => (
                <VStack key={f.id}>
                  <HStack align="center">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      disabled={fields.length === 1}
                      onClick={() => remove(index)}
                    >
                      <CircleMinus />
                    </Button>
                    <HStack
                      gap="0.25rem"
                      align="center"
                      style={{ width: "100%" }}
                    >
                      <Input
                        {...register(`sessions.${index}.name` as const)}
                        aria-invalid={
                          errors.sessions?.[index]?.name ? true : false
                        }
                      />
                      {errors.sessions?.[index]?.name && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <OctagonX
                              style={{
                                color: "var(--danger)",
                                width: "1rem",
                                height: "1rem",
                                cursor: "default",
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            {errors.sessions?.[index]?.name.message}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </HStack>
                    <HStack gap="0.25rem" align="center">
                      <Controller
                        control={control}
                        name={`sessions.${index}.singleton` as const}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onCheckedChange={(v) => field.onChange(v)}
                            aria-invalid={
                              errors.sessions?.[index]?.singleton ? true : false
                            }
                          />
                        )}
                      />
                      {errors.sessions?.[index]?.singleton && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <OctagonX
                              style={{
                                color: "var(--danger)",
                                width: "1rem",
                                height: "1rem",
                                cursor: "default",
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            {errors.sessions?.[index]?.singleton.message}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </HStack>
                  </HStack>
                  {!sessions?.[index]?.singleton && (
                    <HStack
                      gap="0.25rem"
                      align="center"
                      style={{ width: "100%" }}
                    >
                      <Controller
                        control={control}
                        name={`sessions.${index}.selectedDates` as const}
                        render={({ field }) => (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                id="date"
                                className={SelectStyles.trigger}
                                data-placeholder={
                                  field.value?.length ? undefined : ""
                                }
                                aria-invalid={
                                  errors.sessions?.[index]?.selectedDates
                                    ? true
                                    : false
                                }
                                style={{ flex: "1 1 0%" }}
                              >
                                {field.value?.length
                                  ? `${field.value.length} Dates Selected`
                                  : "Select dates"}
                                <ChevronDownIcon
                                  className={SelectStyles.triggerIcon}
                                />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              style={{
                                width: "auto",
                                overflow: "hidden",
                                padding: 0,
                              }}
                              align="start"
                            >
                              <Calendar
                                mode="multiple"
                                selected={field.value}
                                captionLayout="dropdown"
                                onSelect={(date) => {
                                  field.onChange(date);
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                      />
                      {errors.sessions?.[index]?.selectedDates && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <OctagonX
                              style={{
                                color: "var(--danger)",
                                width: "1rem",
                                height: "1rem",
                                cursor: "default",
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            {errors.sessions?.[index]?.selectedDates.message}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </HStack>
                  )}
                </VStack>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  append({ name: "", singleton: false, selectedDates: [] })
                }
                style={{ width: "100%" }}
              >
                Add Session
              </Button>
            </VStack>
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="create-rotation-form"
          style={{ width: "100%" }}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Loading..." : "Create Rotation"}
        </Button>
        <SheetClose asChild>
          <Button
            variant="outline"
            disabled={isSubmitting}
            style={{ width: "100%" }}
          >
            {isSubmitting ? "Loading..." : "Cancel"}
          </Button>
        </SheetClose>
      </SheetFooter>
    </>
  );
};

export { CreateRotationForm };
